<template>
  <div class="container">
    <BasicTitle title="Nuevo Contrato" />
    <BasicSubtitle subtitle="Los campos señalados con (*) son obligatorios." />
    <div class="container-form">
      <BasicLabel label="Usuario Personal *" />
      <BorderSelect
        v-model="contract.data.personnelId"
        label="Usuario Personal *"
        :options="store.state.personnel.personnelList"
      />
      <FormError
        :show="contract.rules.personnelId"
        message="Seleccione un usuario personal"
      />
      <BasicLabel label="Contrato Indefinido *" />
      <BorderSelect
        v-model="contract.data.indExpired"
        label="Contrato Indefinido *"
        :options="contract.expired"
      />
      <FormError
        :show="contract.rules.indExpired"
        message="Seleccione una opción"
      />
      <BasicLabel
        v-if="contract.data.indExpired === 2"
        label="Fecha de Vto *"
      />
      <BorderDate
        v-if="contract.data.indExpired === 2"
        v-model="contract.data.expiredDate"
        label="Fecha de Vto *"
      />
      <FormError
        v-if="contract.data.indExpired === 2"
        :show="contract.rules.expiredDate"
        message="Seleccione una fecha"
      />
      <BasicLabel label="Comentarios" />
      <OutlinedTextArea v-model="contract.data.comments" />
      <FormError />
      <BorderFile
        :v-model="contract.selectedDocuments"
        @select="onSelectFiles"
      />
      <FormError
        :show="contract.rules.selectedDocuments"
        message="Debe adjuntar un documento"
      />
    </div>
    <PrimaryButton label="Guardar" :click="onSave" />
  </div>
</template>

<script>
import BasicTitle from "../../widgets/title/BasicTitle";
import BasicLabel from "../../widgets/label/BasicLabel";
import BasicSubtitle from "../../widgets/subtitle/BasicSubtitle";
import BorderInput from "../../widgets/input/BorderInput";
import BorderSelect from "../../widgets/select/BorderSelect";
import BorderInputPrefix from "../../widgets/input/BorderInputPrefix";
import BorderInputPhone from "../../widgets/input/BorderInputPhone";
import FormError from "../../widgets/error/FormError";
import PrimaryButton from "../../widgets/button/PrimaryButton";
import BorderDate from "../../widgets/calendar/BorderDate.vue";
import BorderTime from "../../widgets/calendar/BorderTime.vue";
import OutlinedTextArea from "../../widgets/textarea/OutlinedTextArea.vue";
import store from "../../store";
import { onBeforeMount, onBeforeUnmount, reactive, watchEffect } from "vue";
import { useRouter, useRoute } from "vue-router";
import { openMode } from "../../constants";
import { validation, actions, aws } from "../../constants";
import BorderFile from "../../widgets/file/BorderFile.vue";

export default {
  components: {
    BasicTitle,
    BasicLabel,
    BasicSubtitle,
    BorderInput,
    BorderSelect,
    BorderInputPrefix,
    BorderInputPhone,
    BorderDate,
    BorderTime,
    FormError,
    PrimaryButton,
    BorderFile,
    OutlinedTextArea,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const contract = reactive({
      selectedDocuments: [],
      expired: [
        { id: 1, name: "Sí" },
        { id: 2, name: "No" },
      ],
      data: {
        id: -1,
        personnelId: "",
        indExpired: 1,
        expiredDate: "",
        comments: "",
      },
      rules: {
        personnelId: false,
        indExpired: false,
        expiredDate: false,
        selectedDocuments: false,
      },
    });

    const onSelectFiles = (ev) => {
      contract.selectedDocuments = ev.files;
    };

    const validate = () => {
      let valid = true;

      if (!contract.data.personnelId) {
        contract.rules.personnelId = true;
        valid = false;
      }
      if (!contract.data.indExpired) {
        contract.rules.indExpired = true;
        valid = false;
      }
      if (contract.data.indExpired === 2 && !contract.data.expiredDate) {
        contract.rules.expiredDate = true;
        valid = false;
      }
      if (!contract.selectedDocuments.length) {
        contract.rules.selectedDocuments = true;
        valid = false;
      }

      return valid;
    };

    const onSave = async () => {
      if (!validate()) return;

      store.commit("setLoading", true);

      const responsePath = await store.dispatch(
        actions.documentActions.getPath,
        {
          buildingId: store.state.general.selectedBuilding,
          folderName: "Contratos",
          documentTypeName: "Contratos",
        }
      );

      if (responsePath.ok) {
        let count = 0;
        const documents = [];
        const { length } = contract.selectedDocuments;
        const finish = async () => {
          const request = {
            id: contract.data.id,
            personnelId: contract.data.personnelId,
            buildingId: store.state.general.selectedBuilding,
            indExpired: contract.data.indExpired === 2 ? true : false,
            expiredDate:
              contract.data.indExpired === 2 ? contract.data.expiredDate : null,
            comments: contract.data.comments,
            documents,
          };

          const response = await store.dispatch(
            actions.contractActions.create,
            request
          );
          store.commit("setLoading", false);
          if (response.ok) {
            store.commit("setSelectedContracts", []);
            router.push("/contratos/listado");
          } else {
            alert(response.message);
          }
        };

        if (count === length) finish();

        contract.selectedDocuments.forEach(async (doc) => {
          const name = doc.name;
          const key = `${responsePath.path}${name}`;
          const uploaded = async () => {
            documents.push({
              path: responsePath.path,
              name: name
                .split(".")
                .slice(0, -1)
                .join("."),
              ext: validation.extensionRegex.exec(name)[0],
            });

            count += 1;
            if (count === length) finish();
          };
          aws.uploadFile(key, doc, uploaded);
        });
      } else {
        store.state.toast.add({
          severity: "error",
          summary: "",
          detail: responsePath.message,
          life: 5000,
        });
      }
    };

    onBeforeMount(async () => {
      if (!store.state.openMode) router.push("/contratos/listado");
      store.commit("addBreadcrumb", { label: route.name, to: route.path });
      store.commit("setLoading", true);
      await store.dispatch(
        actions.personnelActions.findAll,
        store.state.general.selectedBuilding
      );
      store.commit("setLoading", false);
    });

    onBeforeUnmount(() => {
      store.commit("removeBreadcrumb");
    });

    watchEffect(() => {
      if (contract.data.personnelId) contract.rules.personnelId = false;
      if (contract.data.indExpired) contract.rules.indExpired = false;
      if (contract.data.indExpired === 2 && contract.data.expiredDate)
        contract.rules.expiredDate = false;
      if (contract.selectedDocuments.length)
        contract.rules.selectedDocuments = false;
    });

    return { store, contract, onSave, onSelectFiles };
  },
};
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.container-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2rem 0;
}
.link-label {
  margin-right: 1rem;
  font-family: "klavika";
  color: var(--primary);
}
</style>
